import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CarLeft from "@/assets/illustrations/CarLeft.png";
import CarRight from "@/assets/illustrations/CarRight.png";
import { RecentHistoryTable } from "@/components/custom/recentsTable";
import AllocationService from "@/services/api/AllotmentService";
import { recentHistoryObject } from "@/central-atoms/everest-fleet-pwa/types";
import { BottomNav } from "@/components/custom/BottomNav";
import {
  useHubDetails,
  useUserDetails,
} from "@/redux/hooks/useSupervisorDetails";
import { setCarDetails } from "@/redux/carAllocationSlice";
import { useDispatch } from "react-redux";
import ErrorMessageFullCard from "@/components/custom/ErrorMessageFullCard";

const CheckOut = () => {
  const [historyData, setHistoryData] = useState<recentHistoryObject[]>([]);
  const [token, setToken] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false); // Track API call status
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userDetails = useUserDetails();
  const hubDetails = useHubDetails();

  useEffect(() => {
    const fetchHistoryData = async () => {
      try {
        const response = await AllocationService.getAllotmentHistory(
          hubDetails?.name as string,
          0,
          5
        );

        if (response.success && response.data.success) {
          const formattedData = response.data.data.records.map((item) => ({
            carNumber: item.track_props.car_number as string,
            dateTime: new Date(item.created_on).toLocaleString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }),
          }));
          setHistoryData(formattedData);
        }
      } catch (error) {
        console.error("Failed to fetch allotment history", error);
      }
    };

    fetchHistoryData();
  }, []);

  const handleConfirmClick = async () => {
    if (!userDetails || !userDetails.phone) {
      setErrorMessage(["User details not found. Please try again."]);
      return;
    }

    setIsSubmitting(true); // Disable button

    try {
      const reqBody = { token, phone: userDetails.phone };
      const response = await AllocationService.listCarsFromToken(reqBody);

      if (response.success) {
        const carSelectionLocked = response.car_selection_locked === true;

        // Everest owns the allocation and has not sent a car for this pilot yet, so there is
        // nothing for the supervisor to confirm — show the reason instead of an empty screen.
        if (carSelectionLocked && response.data.sorted_list.length === 0) {
          setErrorMessage(response.data.messages);
          return;
        }

        let carDetails = {
          vrn: "",
          hubName: hubDetails?.name as string,
          soc: 0,
          odo: 0,
          token,
          carSelectionLocked,
        };

        if (response.data.sorted_list.length > 0) {
          carDetails = {
            vrn: response.data.sorted_list[0].vrn,
            hubName: hubDetails?.name as string,
            soc: response.data.sorted_list[0].soc || 0,
            odo: response.data.sorted_list[0].odo || 0,
            token,
            carSelectionLocked,
          };
        }

        dispatch(setCarDetails(carDetails));
        navigate("/check-out/car-allocation");
      } else {
        console.error("Failed to list cars from token", response);
      }
    } catch (error: any) {
      if (error?.response?.data?.data?.messages) {
        setErrorMessage(error?.response?.data?.data?.messages);
      }
      console.error("Failed to list cars from token", error);
    } finally {
      setIsSubmitting(false); // Re-enable button
    }
  };

  return (
    <>
      {errorMessage.length > 0 && (
        <ErrorMessageFullCard
          errorString={errorMessage}
          handleBackToHome={() => {
            setErrorMessage([]);
            navigate("/home");
          }}
        >
          <div className="pb-[10px]">
            <div className="text-secondary font-bold pb-[4px] text-[14px]">
              Login Token: {token}
            </div>
          </div>
        </ErrorMessageFullCard>
      )}
      <div className="bg-secondary min-h-screen flex flex-col items-center gap-y-5">
        <div className="w-full flex justify-start mt-10">
          <img src={CarLeft} className="w-1/2" />
        </div>
        <h1 className="text-secondary-foreground text-center">
          Enter Token Number
        </h1>

        <div className="w-5/6 flex flex-col gap-4">
          <Input
            variant="primary"
            className="text-secondary-foreground w-full border-4"
            value={token}
            type="number"
            onChange={(e) => setToken(e.target.value)}
          />
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={handleConfirmClick}
            disabled={isSubmitting} // Disable when API call is ongoing
          >
            {isSubmitting ? "Loading..." : "Confirm"}
          </Button>
        </div>

        <div className="w-5/6 rounded-2xl bg-[#0a4762]">
          <h3 className="flex flex-row justify-start text-white px-4 p-3">
            Recent History
          </h3>
          <RecentHistoryTable data={historyData} />
        </div>
      </div>

      <BottomNav active={3} />
    </>
  );
};

export default CheckOut;
